import React, { useCallback, useEffect, useReducer } from "react";

import useDeepCompareEffect from "use-deep-compare-effect";

import { toDisplay, toValue } from "@covertree/utils";

export enum Action {
  CHANGE_DAY = "CHANGE_DAY",
  CHANGE_MONTH = "CHANGE_MONTH",
  CHANGE_YEAR = "CHANGE_YEAR",
  RESET = "RESET",
  CLEAR = "CLEAR",
}

export interface ActionTypeWithValue {
  type: Action.CHANGE_DAY | Action.CHANGE_MONTH | Action.CHANGE_YEAR;
  value: string;
}

export interface ActionTypeWithoutValue {
  type: Action.RESET;
}

export type ActionType = ActionTypeWithValue | ActionTypeWithoutValue;

export interface DateState {
  day: string;
  month: string;
  year: string;
  value: string;
}

export interface DateContextType extends DateState {
  onChange?(value: string): void;
  changeMonth(month: string): void;
  changeYear(month: string): void;
  changeDay(month: string): void;
  reset(): void;
}

const reducer: React.Reducer<DateState, ActionType> = (state, action) => {
  switch (action.type) {
    case Action.CHANGE_DAY:
      return {
        ...state,
        day: action.value,
        value: toValue({ ...state, day: action.value }),
      };

    case Action.CHANGE_MONTH:
      return {
        ...state,
        month: action.value,
        value: toValue({ ...state, month: action.value }),
      };

    case Action.CHANGE_YEAR:
      return {
        ...state,
        year: action.value,
        value: toValue({ ...state, year: action.value }),
      };

    case Action.RESET:
      return {
        day: "",
        month: "",
        year: "",
        value: "",
      };

    default:
      return state;
  }
};

export const DateContext = React.createContext<DateContextType>({
  value: "",
  day: "",
  month: "",
  year: "",
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange(_) {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  changeDay(_) {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  changeMonth(_) {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  changeYear(_) {},
  reset() {},
});

export const DateConsumer = DateContext.Consumer;

interface Props {
  value?: string;
  isTouched?: boolean;
  onChange?(value: string): void;
  children: React.ReactNode;
}

export const DateProvider: React.FC<Props> = ({
  value = "",
  isTouched,
  children,
  onChange,
}) => {
  const display = toDisplay(value);

  const initialState: DateState = {
    ...display,
    value,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  /*
   * Custom logic to reset the date if the value is empty
   */
  useEffect(() => {
    const newDisplay = toDisplay(value);

    if (newDisplay.day || newDisplay.month || newDisplay.year) return;

    dispatch({ type: Action.CHANGE_DAY, value: newDisplay.day });
    dispatch({ type: Action.CHANGE_MONTH, value: newDisplay.month });
    dispatch({ type: Action.CHANGE_YEAR, value: newDisplay.year });
  }, [value]);

  const changeDay = useCallback(
    (day: string) => dispatch({ type: Action.CHANGE_DAY, value: day }),
    [dispatch]
  );

  const changeMonth = useCallback(
    (month: string) => dispatch({ type: Action.CHANGE_MONTH, value: month }),
    [dispatch]
  );

  const changeYear = useCallback(
    (year: string) => dispatch({ type: Action.CHANGE_YEAR, value: year }),
    [dispatch]
  );

  const reset = useCallback(() => dispatch({ type: Action.RESET }), [dispatch]);

  useDeepCompareEffect(() => {
    if (state.value || isTouched) {
      onChange?.(state.value);
    }
  }, [state]);

  return (
    <DateContext.Provider
      value={{
        ...state,
        onChange,
        changeDay,
        changeMonth,
        changeYear,
        reset,
      }}>
      {children}
    </DateContext.Provider>
  );
};
